import React from 'react';
import { cn } from '../../utils/cn';
import { FDI_UPPER, FDI_LOWER, fdiToUniversal } from './fdiToothMapping';

/**
 * Une seule silhouette dentaire pour toutes les positions ISO — couronne arrondie,
 * racines douces (vue stylisée frontale).
 */
function ToothIcon({ className, selected }) {
  return (
    <svg
      viewBox="0 0 32 40"
      className={cn(
        'w-7 h-8 sm:w-8 sm:h-9 shrink-0 transition-colors duration-150',
        className
      )}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M16 2.2c-4.2 0-7.7 2.5-8.8 6-.9 2.7-.7 5.5.2 8.2l1.4 8.6c.5 2.9 1.4 5 2.8 6.5.9 1 1.9 1.5 3 1.5 1.4 0 2.4-.7 3.2-1.8.3-.4.5-.8.7-1.2l.7-1.8.7 1.8c.2.4.4.8.7 1.2.8 1.1 1.8 1.8 3.2 1.8 1.1 0 2.1-.5 3-1.5 1.4-1.5 2.3-3.6 2.8-6.5l1.4-8.6c.9-2.7 1.1-5.5.2-8.2-1.1-3.5-4.6-6-8.8-6z"
        className={cn(
          selected
            ? 'fill-primary/30 stroke-primary stroke-[1.45]'
            : 'fill-white dark:fill-slate-800 stroke-slate-400 dark:stroke-slate-500 stroke-[1.15]'
        )}
      />
      <path
        d="M10 14c2 2 4 3 6 3s4-1 6-3"
        className={cn(
          'fill-none',
          selected ? 'stroke-primary/45 stroke-[0.75]' : 'stroke-slate-400/50 stroke-[0.65]'
        )}
      />
      <path
        d="M16 8v6"
        className={cn(
          selected ? 'stroke-primary/25' : 'stroke-slate-400/35',
          'stroke-[0.5]'
        )}
      />
    </svg>
  );
}

/**
 * Schéma dentaire ISO (FDI), 32 dents, cliquable. Stockage côté parent : universal 1–32.
 * @param {number[]} selectedUniversal dents cochées (1–32)
 * @param {(universal: number) => void} onToggle
 */
const DentalChartIso = ({
  selectedUniversal = [],
  onToggle,
  className,
  idPrefix = 'tooth',
}) => {
  const selected = new Set(selectedUniversal);
  return (
    <div
      className={cn(
        'rounded-xl border border-border/80 bg-card/50 dark:bg-card/30 px-2 py-3 sm:px-4',
        className
      )}
    >
      <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground font-medium mb-1 px-0.5">
        <span>Droite (patient)</span>
        <span>Gauche (patient)</span>
      </div>
      <div className="h-px w-full bg-border/70 mb-2" />
      <div className="flex flex-col gap-3">
        <div className="space-y-1 overflow-x-auto -mx-1 px-1 pb-1">
          <div className="flex justify-center items-end gap-0.5 min-w-min mx-auto">
            {FDI_UPPER.map((fdi) => {
              const u = fdiToUniversal(fdi);
              const isOn = u != null && selected.has(u);
              return (
                <button
                  key={fdi}
                  type="button"
                  onClick={() => u != null && onToggle(u)}
                  className={cn(
                    'group flex flex-col items-center p-0.5 rounded-md transition-transform active:scale-95',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                    isOn && 'ring-1 ring-primary/30 rounded-md bg-primary/5'
                  )}
                  id={`${idPrefix}-fdi-${fdi}`}
                >
                  <span
                    className={cn(
                      'text-[9px] sm:text-[10px] leading-none font-semibold tabular-nums mb-0.5',
                      isOn ? 'text-primary' : 'text-muted-foreground'
                    )}
                  >
                    {fdi}
                  </span>
                  <ToothIcon selected={isOn} />
                </button>
              );
            })}
          </div>
        </div>
        <div className="h-px w-full bg-border/50" />
        <div className="overflow-x-auto -mx-1 px-1 pt-1">
          <div className="flex justify-center items-start gap-0.5 min-w-min mx-auto">
            {FDI_LOWER.map((fdi) => {
              const u = fdiToUniversal(fdi);
              const isOn = u != null && selected.has(u);
              return (
                <button
                  key={fdi}
                  type="button"
                  onClick={() => u != null && onToggle(u)}
                  className={cn(
                    'group flex flex-col items-center p-0.5 rounded-md transition-transform active:scale-95',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                    isOn && 'ring-1 ring-primary/30 rounded-md bg-primary/5'
                  )}
                  id={`${idPrefix}-fdi-${fdi}`}
                >
                  <ToothIcon selected={isOn} className="rotate-180" />
                  <span
                    className={cn(
                      'text-[9px] sm:text-[10px] leading-none font-semibold tabular-nums mt-0.5',
                      isOn ? 'text-primary' : 'text-muted-foreground'
                    )}
                  >
                    {fdi}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-[9px] text-right text-muted-foreground mt-2">Notation ISO (FDI)</p>
    </div>
  );
};

export default DentalChartIso;
